import Phaser from 'phaser';
import { CardTransition } from '../objects/CardTransition';

/**
 * Transition colors (RGB format for Phaser camera fade)
 */
export const TRANSITION_COLORS = {
  feltGreen: { r: 26, g: 74, b: 58 }, // #1a4a3a - main game background
  darkOverlay: { r: 13, g: 31, b: 25 }, // #0d1f19 - dramatic dark
  black: { r: 0, g: 0, b: 0 }, // #000000 - clean reset
};

/**
 * Default transition durations (ms)
 */
export const TRANSITION_DURATIONS = {
  default: 400,
  toGame: 500, // Building anticipation
  toGameOver: 600, // Dramatic pause
  tolobby: 400, // Clean reset
};

type TransitionColor = keyof typeof TRANSITION_COLORS;

/**
 * Utility class for smooth scene transitions with fade effects.
 */
export class SceneTransition {
  /**
   * Fade out current scene and transition to target scene.
   * @param scene - Current scene instance
   * @param targetScene - Name of the scene to transition to
   * @param options - Transition options
   */
  static fadeTo(
    scene: Phaser.Scene,
    targetScene: string,
    options: {
      duration?: number;
      color?: TransitionColor;
      data?: object;
    } = {}
  ): void {
    const { duration = TRANSITION_DURATIONS.default, color = 'feltGreen', data } = options;

    const { r, g, b } = TRANSITION_COLORS[color];

    // Prevent multiple transitions (check if fade effect is active)
    const fadeEffect = scene.cameras.main.fadeEffect;
    if (fadeEffect && fadeEffect.isRunning) return;

    scene.cameras.main.fade(duration, r, g, b);
    scene.cameras.main.once('camerafadeoutcomplete', () => {
      scene.scene.start(targetScene, data);
    });
  }

  /**
   * Fade in when scene starts. Call this in the scene's create() method.
   * @param scene - Current scene instance
   * @param options - Fade options
   */
  static fadeIn(
    scene: Phaser.Scene,
    options: {
      duration?: number;
      color?: TransitionColor;
    } = {}
  ): void {
    const { duration = TRANSITION_DURATIONS.default, color = 'feltGreen' } = options;

    const { r, g, b } = TRANSITION_COLORS[color];

    scene.cameras.main.fadeIn(duration, r, g, b);
  }

  /**
   * Cards fly in from edges and converge to center.
   * Use when entering a game room.
   * @param scene - Current scene instance
   * @param onComplete - Callback when animation completes
   */
  static cardFlyIn(scene: Phaser.Scene, onComplete?: () => void): CardTransition {
    const transition = new CardTransition(scene);
    transition.flyIn(onComplete);
    return transition;
  }

  /**
   * Cards scatter from center to edges with fade.
   * Use when leaving a game room.
   * @param scene - Current scene instance
   * @param targetScene - Scene to transition to after animation
   * @param data - Optional data to pass to target scene
   */
  static cardFlyOut(scene: Phaser.Scene, targetScene: string, data?: object): void {
    const transition = new CardTransition(scene);
    transition.flyOut(() => {
      transition.destroy();
      scene.scene.start(targetScene, data);
    });
  }

  /**
   * Crossfade between two sets of containers - fade out one set while fading in another.
   * Used for smooth UI transitions (e.g., lobby join UI to room UI).
   * @param scene - Current scene instance
   * @param fadeOut - Containers to fade out (alpha 1 -> 0)
   * @param fadeIn - Containers to fade in (alpha 0 -> 1)
   * @param duration - Transition duration in ms
   */
  static crossfade(
    scene: Phaser.Scene,
    fadeOut: Phaser.GameObjects.Container[],
    fadeIn: Phaser.GameObjects.Container[],
    duration: number = 300
  ): Promise<void> {
    return new Promise((resolve) => {
      // Set initial states
      fadeIn.forEach((container) => {
        container.setAlpha(0);
        container.setVisible(true);
      });

      // Fade out containers
      fadeOut.forEach((container) => {
        scene.tweens.add({
          targets: container,
          alpha: 0,
          duration,
          ease: 'Quad.easeInOut',
          onComplete: () => container.setVisible(false),
        });
      });

      // Fade in containers
      let completed = 0;
      fadeIn.forEach((container) => {
        scene.tweens.add({
          targets: container,
          alpha: 1,
          duration,
          ease: 'Quad.easeInOut',
          onComplete: () => {
            completed++;
            if (completed === fadeIn.length) {
              resolve();
            }
          },
        });
      });

      // Resolve immediately if nothing to fade in
      if (fadeIn.length === 0) {
        scene.time.delayedCall(duration, resolve);
      }
    });
  }
}
